import React, { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import "./PageLayout.css";
import "./RefertiOnline.css";
import logger from "../services/Logger";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as signalR from "@microsoft/signalr";

// Usa i nuovi hooks e services
import { useDocuments, useErrorHandler } from "../hooks/useServices";
import { Report } from "../types";
import ConfigService from "../services/ConfigService";
import DocumentService from "../services/api/DocumentService";

interface DicomCacheEntry {
  available: boolean;
  studyUIDs?: string[];
  instanceUIDs?: string[]; // Aggiungi instanceUIDs
  patientID?: string;
  timestamp: number;
  expires: number;
}

type LocalDicomJobProgress = {
  status: string;
  progress: number;
  error?: string;
  errorMessage?: string;
  jobId?: string;
  studyUID?: string;
  downloading?: boolean;
  zipFilePath?: string;
};

const RefertiOnline: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Lista amministratori (stessi di Account.tsx)
  const ADMIN_USERS = ["FRSRFL72R25H282U", "GRRLCU88P05H501J"];

  // Helper functions to get URLs - always gets fresh URL from ConfigService
  const getPublishAndDownloadJobUrl = () => ConfigService.buildApiUrl("DICOM/PublishAndDownloadJob");
  const getDownloadZipUrl = () => ConfigService.buildApiUrl("DICOM/DownloadZip");
  const getSignalRExportJobHubUrl = () => ConfigService.buildUrl("ExportJobHub");
  const getRepublishReportUrl = () => ConfigService.buildApiUrl("Document/republish");
  const getRepublishRequestUrl = () => ConfigService.buildApiUrl("Document/republish-request");

  // Stato referti e PDF
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfCache, setPdfCache] = useState<Record<string, string>>({});
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [lastRepublishedEpisodeId, setLastRepublishedEpisodeId] = useState<
    string | null
  >(null);
  const [republishInProgress, setRepublishInProgress] = useState<string | null>(
    null
  );
  const [reportsLoaded, setReportsLoaded] = useState(false); // Flag per cache referti

  // Stato per forzatura notifica (admin only)
  const [forceNotifyDialogVisible, setForceNotifyDialogVisible] =
    useState(false);
  const [forceNotifyEpisodeId, setForceNotifyEpisodeId] = useState("");
  const [forceNotifyLoading, setForceNotifyLoading] = useState(false);
  const [forceNotifyConfirmData, setForceNotifyConfirmData] = useState<any>(
    null
  );

  // Stato DICOM
  const [studyUIDsMap, setStudyUIDsMap] = useState<Record<string, string[]>>(
    {}
  );
  const [instanceUIDsMap, setInstanceUIDsMap] = useState<
    Record<string, string[]>
  >({}); // Per contare le serie DICOM
  const [patientIdMap, setPatientIdMap] = useState<Record<string, string>>({});
  const [dicomJobProgress, setDicomJobProgress] = useState<
    Record<string, LocalDicomJobProgress>
  >({});
  const [dicomCheckInProgress, setDicomCheckInProgress] = useState<
    Record<string, boolean>
  >({});
  const jobPollingIntervals = useRef<Record<string, NodeJS.Timeout>>({});
  const [dicomAvailability, setDicomAvailability] = useState<
    Record<string, boolean | undefined>
  >({});

  // Cache DICOM con timeout (30 minuti)
  const dicomCacheRef = useRef<Record<string, DicomCacheEntry>>({});
  const DICOM_CACHE_TTL = 30 * 60 * 1000; // 30 minuti in millisecondi
  const DICOM_CHECK_TIMEOUT = 15000; // 15 secondi timeout per chiamata (aumentato da 5)

  // Redux (token e username)
  const token = useSelector((state: RootState) => state.user.token);
  const username = useSelector((state: RootState) => state.user.codiceFiscale);

  // Usa i nuovi hooks
  const { getReports, downloadReport, checkDicomImages } = useDocuments();
  useErrorHandler(toast); // Configura error handler con toast

  // Carica i referti usando il nuovo service
  useEffect(() => {
    if (username && !reportsLoaded) {
      fetchReports();
      setReportsLoaded(true);
    }
    // eslint-disable-next-line
  }, [username]);

  // Funzione per creare una Promise con timeout
  const withTimeout = <T,>(
    promise: Promise<T>,
    timeoutMs: number
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Timeout di ${timeoutMs}ms raggiunto`));
      }, timeoutMs);

      promise
        .then(resolve)
        .catch(reject)
        .finally(() => clearTimeout(timeoutId));
    });
  };

  // Funzione per verificare se un episodio è in cache e non scaduto
  const getDicomFromCache = (episodeId: string): DicomCacheEntry | null => {
    const cached = dicomCacheRef.current[episodeId];
    if (cached && Date.now() < cached.expires) {
      return cached;
    }
    return null;
  };

  // Funzione per salvare nella cache DICOM
  const saveDicomToCache = (
    episodeId: string,
    available: boolean,
    studyUIDs?: string[],
    instanceUIDs?: string[],
    patientID?: string
  ) => {
    const now = Date.now();
    dicomCacheRef.current[episodeId] = {
      available,
      studyUIDs,
      instanceUIDs,
      patientID,
      timestamp: now,
      expires: now + DICOM_CACHE_TTL,
    };
  };

  // Funzione helper per determinare se è un referto di laboratorio
  // I referti di laboratorio non hanno resultsIds
  const isLabReport = (report: Report): boolean => {
    return !report.resultsIds || report.resultsIds.trim() === "";
  };

  // Verifica automatica disponibilità DICOM per i referti firmati NON di laboratorio
  useEffect(() => {
    const checkAvailability = async () => {
      if (reports.length === 0 || !token) return;

      // Filtra solo i referti firmati che non sono già stati verificati e non sono in cache
      const reportsToCheck = reports.filter((report) => {
        const episodeId = report.episodeId;

        // Salta se già verificato
        if (dicomAvailability[episodeId] !== undefined) return false;

        // Salta se non è firmato (examinationState >= 2)
        if (!isReportSigned(report)) {
          setDicomAvailability((prev) => ({ ...prev, [episodeId]: false }));
          return false;
        }

        // OTTIMIZZAZIONE: Salta i referti di laboratorio (resultsIds vuoto)
        if (isLabReport(report)) {
          console.log(
            `[DICOM] Skipping lab report ${episodeId} - No resultsIds (${report.workareasString})`
          );
          setDicomAvailability((prev) => ({ ...prev, [episodeId]: false }));
          return false;
        }

        // Controlla cache
        const cached = getDicomFromCache(episodeId);
        if (cached) {
          setDicomAvailability((prev) => ({
            ...prev,
            [episodeId]: cached.available,
          }));
          if (cached.available && cached.studyUIDs && cached.patientID) {
            setStudyUIDsMap((prev) => ({
              ...prev,
              [episodeId]: cached.studyUIDs!,
            }));
            setInstanceUIDsMap((prev) => ({
              ...prev,
              [episodeId]: cached.instanceUIDs || [],
            }));
            setPatientIdMap((prev) => ({
              ...prev,
              [episodeId]: cached.patientID!,
            }));
          }
          return false;
        }

        return true;
      });

      if (reportsToCheck.length === 0) return;

      // DEBUG: Log dettagliato per episodio specifico
      const targetReport = reports.find((r) => r.episodeId === "210720250458");
      if (targetReport) {
        const willBeChecked = reportsToCheck.some(
          (r) => r.episodeId === "210720250458"
        );
        console.log(`[DICOM DEBUG] Episode 210720250458 details:`, {
          episodeId: targetReport.episodeId,
          resultsIds: targetReport.resultsIds,
          workareasString: targetReport.workareasString,
          examinationState: targetReport.examinationState,
          isSigned: isReportSigned(targetReport),
          isLabReport: isLabReport(targetReport),
          willCheckDICOM: willBeChecked,
        });
        if (!willBeChecked) {
          if (isLabReport(targetReport)) {
            console.log(
              `[DICOM DEBUG] SALTATO - Considerato lab report perché resultsIds è vuoto/null`
            );
          } else if (!isReportSigned(targetReport)) {
            console.log(
              `[DICOM DEBUG] SALTATO - Non completo/parziale (examinationState != 5 e != 2)`
            );
          } else {
            console.log(`[DICOM DEBUG] SALTATO - Già verificato o in cache`);
          }
        } else {
          console.log(`[DICOM DEBUG] PROCEDE con check DICOM per 210720250458`);
        }
      }

      // Imposta stato "in progress" per tutti i referti da verificare
      const episodeIds = reportsToCheck.map((r) => r.episodeId);
      setDicomCheckInProgress((prev) => {
        const newState = { ...prev };
        episodeIds.forEach((id) => (newState[id] = true));
        return newState;
      });

      // Crea le promise per tutte le verifiche DICOM con timeout
      const checkPromises = reportsToCheck.map(async (report) => {
        const episodeId = report.episodeId;
        try {
          console.log(
            `[DICOM] Checking availability for ${episodeId} (${
              report.workareasString || "N/A"
            })`
          );

          // Applica timeout di 5 secondi alla chiamata
          const response = await withTimeout(
            checkDicomImages(episodeId),
            DICOM_CHECK_TIMEOUT
          );

          if (response && response.available) {
            console.log(`[DICOM] Images available for ${episodeId}`);
            // Salva in cache
            saveDicomToCache(
              episodeId,
              true,
              response.studyUIDs,
              response.instanceUIDs,
              response.patientID || report.pid
            );
            return {
              episodeId,
              available: true,
              studyUIDs: response.studyUIDs || [],
              instanceUIDs: response.instanceUIDs || [],
              patientID: response.patientID || report.pid || "",
            };
          } else {
            console.log(`[DICOM] No images for ${episodeId}`);
            // Salva in cache
            saveDicomToCache(episodeId, false, [], [], "");
            return {
              episodeId,
              available: false,
            };
          }
        } catch (error) {
          // Retry speciale per episodio 210720250458
          if (
            episodeId === "210720250458" &&
            error instanceof Error &&
            error.message.includes("Timeout")
          ) {
            console.warn(
              `[DICOM] Timeout per 210720250458, riprovo con timeout più lungo...`
            );
            try {
              // Riprova con timeout più lungo (30 secondi)
              const retryPromise = checkDicomImages(episodeId);
              const retryResponse = await withTimeout(retryPromise, 30000);
              console.log(
                `[DICOM] Retry success for 210720250458:`,
                retryResponse
              );
              const available = retryResponse?.available === true;

              if (
                available &&
                retryResponse?.studyUIDs &&
                retryResponse?.patientID
              ) {
                saveDicomToCache(
                  episodeId,
                  available,
                  retryResponse.studyUIDs,
                  retryResponse.instanceUIDs,
                  retryResponse.patientID
                );
                return {
                  episodeId,
                  available,
                  studyUIDs: retryResponse.studyUIDs,
                  instanceUIDs: retryResponse.instanceUIDs || [],
                  patientID: retryResponse.patientID,
                };
              } else {
                saveDicomToCache(episodeId, false, [], [], "");
                return { episodeId, available: false };
              }
            } catch (retryError) {
              console.error(
                `[DICOM] Retry failed for 210720250458:`,
                retryError
              );
            }
          }

          console.error(`[DICOM] Error checking ${episodeId}:`, error);
          // Salva in cache come non disponibile in caso di errore
          saveDicomToCache(episodeId, false, [], [], "");
          return {
            episodeId,
            available: false,
            error:
              error instanceof Error ? error.message : "Errore sconosciuto",
          };
        }
      });

      // Esegui tutte le verifiche in parallelo usando Promise.allSettled
      const results = await Promise.allSettled(checkPromises);

      // Processa i risultati
      results.forEach((result, index) => {
        const episodeId = reportsToCheck[index].episodeId;

        if (result.status === "fulfilled") {
          const checkResult = result.value;
          setDicomAvailability((prev) => ({
            ...prev,
            [checkResult.episodeId]: checkResult.available,
          }));

          if (
            checkResult.available &&
            checkResult.studyUIDs &&
            checkResult.patientID
          ) {
            setStudyUIDsMap((prev) => ({
              ...prev,
              [checkResult.episodeId]: checkResult.studyUIDs!,
            }));
            setInstanceUIDsMap((prev) => ({
              ...prev,
              [checkResult.episodeId]: checkResult.instanceUIDs || [],
            }));
            setPatientIdMap((prev) => ({
              ...prev,
              [checkResult.episodeId]: checkResult.patientID!,
            }));
          }
        } else {
          console.error(
            `[DICOM] Promise rejected for ${episodeId}:`,
            result.reason
          );
          setDicomAvailability((prev) => ({ ...prev, [episodeId]: false }));
        }

        // Rimuovi stato "in progress"
        setDicomCheckInProgress((prev) => ({ ...prev, [episodeId]: false }));
      });
    };

    checkAvailability();
  }, [reports, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReports = async () => {
    if (!username) {
      setError(
        "Utente non autenticato. Effettua il login per visualizzare i referti."
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getReports(username);

      // LOG: Verifica cosa viene restituito dall'API
      console.log("🔍 API Response - Raw data:", data);
      if (data && data.length > 0) {
        console.log("🔍 First report structure:", data[0]);
        console.log("🔍 CompanyWebSite field:", data[0].companyWebSite);
      }

      // Recupera il filtro company dalla configurazione
      const config = ConfigService.getConfig();
      const companyFilter = config?.reports?.reportsCompanyFilter || "";

      // Crea array di company da escludere (trimma gli spazi e filtra valori vuoti)
      const companiesToExclude = companyFilter
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      console.log("🔍 Company filter from config:", companiesToExclude);

      // Filtra i referti escludendo quelli con companyWebSite nell'elenco
      // NOTA: Trim del companyWebSite perché il DB potrebbe avere spazi trailing
      const filteredData = data.filter(
        (report) =>
          !companiesToExclude.includes((report.companyWebSite || "").trim())
      );

      // Ordina i referti per data di registrazione (più recente prima)
      // DEBUG: Log specifico per episodio 210720250458
      const targetReport = filteredData.find(
        (r) => r.episodeId === "210720250458"
      );
      if (targetReport) {
        console.log("=== REFERTO 210720250458 TROVATO ===");
        console.log("Dati completi:", targetReport);
        console.log("resultsIds:", targetReport.resultsIds);
        console.log(
          "resultsIds è vuoto?",
          !targetReport.resultsIds || targetReport.resultsIds.trim() === ""
        );
        console.log("examinationState:", targetReport.examinationState);
        console.log(
          "È firmato (state >= 2)?",
          targetReport.examinationState
            ? targetReport.examinationState >= 2
            : false
        );
        console.log("workareasString:", targetReport.workareasString);
        console.log(
          "Verrebbe considerato Lab Report?",
          !targetReport.resultsIds || targetReport.resultsIds.trim() === ""
        );
        console.log("=====================================");
      } else {
        console.log(
          "REFERTO 210720250458 NON TROVATO nei dati ricevuti (potrebbe essere stato filtrato per company CIN)"
        );
      }

      const sorted = [...filteredData].sort((a, b) => {
        const parseDate = (d: string | Date | undefined) => {
          if (!d) return new Date(0);
          if (d instanceof Date) return d;

          const parts = d.split("/");
          if (parts.length === 3 && parts[2].length === 4) {
            return new Date(+parts[2], +parts[1] - 1, +parts[0]);
          }
          return new Date(d);
        };

        const dateA = parseDate(a.registrationDate);
        const dateB = parseDate(b.registrationDate);
        return dateB.getTime() - dateA.getTime();
      });

      setReports(sorted);

      // Log dello stato di ogni referto per debug
      console.log("📊 Stati dei referti ricevuti dall'API:");
      sorted.forEach((referto: Report, index: number) => {
        console.log(`Referto ${index + 1} (${referto.episodeId}):`, {
          examinationState: referto.examinationState,
          examName: referto.examName,
          registrationDate: referto.registrationDate,
          workareasString: referto.workareasString,
          statoInterpretato:
            referto.examinationState === 5
              ? "Completo"
              : referto.examinationState === 2
              ? "Parziale"
              : referto.examinationState === undefined
              ? "UNDEFINED - campo non presente"
              : `ALTRO VALORE: ${referto.examinationState}`,
        });
      });
      console.log("📊 Fine log stati referti - Totale:", sorted.length);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Errore sconosciuto";
      setError(errorMessage);
      logger.error("Errore caricamento referti:", err);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per calcolare se un referto è scaduto (> 45 giorni)
  const isReportExpired = (dateStr?: string | Date) => {
    if (!dateStr) return false;

    let reportDate: Date;

    if (dateStr instanceof Date) {
      reportDate = dateStr;
    } else {
      // Match dd/MM/yyyy HH:mm
      const parts = dateStr.split(/[\/ :]/);
      if (parts.length >= 3) {
        const day = +parts[0];
        const month = +parts[1] - 1;
        const year = +parts[2];
        const hour = parts[3] ? +parts[3] : 0;
        const minute = parts[4] ? +parts[4] : 0;
        reportDate = new Date(year, month, day, hour, minute, 0);
      } else {
        reportDate = new Date(dateStr);
      }
    }

    if (isNaN(reportDate.getTime())) return false;

    const now = new Date();
    const days = (now.getTime() - reportDate.getTime()) / (1000 * 60 * 60 * 24);
    return days > 45;
  };

  const isRepublished = (
    publishDateStr?: string | Date,
    registrationDateStr?: string | Date
  ) => {
    if (!publishDateStr || !registrationDateStr) return false;

    const parseDate = (dateStr: string | Date) => {
      if (dateStr instanceof Date) return dateStr;

      const parts = dateStr.split(/[\/ :]/);
      if (parts.length >= 3) {
        const day = +parts[0];
        const month = +parts[1] - 1;
        const year = +parts[2];
        const hour = parts[3] ? +parts[3] : 0;
        const minute = parts[4] ? +parts[4] : 0;
        return new Date(year, month, day, hour, minute, 0);
      }
      return new Date(dateStr);
    };

    const regDate = parseDate(registrationDateStr);
    const pubDate = parseDate(publishDateStr);
    const days =
      (pubDate.getTime() - regDate.getTime()) / (1000 * 60 * 60 * 24);
    return days > 45;
  };

  // Determina se un referto è completo/firmato basandosi su examinationState
  // ExaminationState: 5 = Completo, 2 = Parziale
  const isReportSigned = (report: Report): boolean => {
    // Se abbiamo refertSigned, usalo
    if (report.refertSigned !== undefined) {
      return report.refertSigned;
    }
    // Stati 5 (Completo) o 2 (Parziale) indicano che il referto è disponibile
    return report.examinationState === 5 || report.examinationState === 2;
  };

  // Verifica se l'utente corrente è amministratore
  const isAdmin = (): boolean => {
    return ADMIN_USERS.includes(username?.toUpperCase() || "");
  };

  // Gestisce la forzatura della notifica di un referto (solo admin)
  const handleForceNotify = async () => {
    if (!forceNotifyEpisodeId || forceNotifyEpisodeId.trim() === "") {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Inserisci il codice accettazione del referto",
        life: 3000,
      });
      return;
    }

    setForceNotifyLoading(true);

    try {
      // Prima chiamata senza confirmed per ottenere i dettagli
      const response = await DocumentService.forceNotifyReport(
        forceNotifyEpisodeId,
        false
      );

      if (response.requiresConfirmation) {
        // Mostra conferma con dettagli
        setForceNotifyConfirmData(response);
        toast.current?.show({
          severity: "warn",
          summary: "Conferma necessaria",
          detail: response.message,
          life: 5000,
        });
      } else if (response.success) {
        // Successo diretto
        toast.current?.show({
          severity: "success",
          summary: "Notifica forzata",
          detail: response.message,
          life: 3000,
        });
        setForceNotifyDialogVisible(false);
        setForceNotifyEpisodeId("");
        setForceNotifyConfirmData(null);
        // Ricarica i referti
        fetchReports();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: error.message || "Impossibile forzare la notifica",
        life: 5000,
      });
    } finally {
      setForceNotifyLoading(false);
    }
  };

  // Conferma forzatura notifica dopo aver visto i dettagli
  const confirmForceNotify = async () => {
    setForceNotifyLoading(true);

    try {
      const response = await DocumentService.forceNotifyReport(
        forceNotifyEpisodeId,
        true
      );

      if (response.success) {
        toast.current?.show({
          severity: "success",
          summary: "Notifica forzata",
          detail: response.message,
          life: 3000,
        });
        setForceNotifyDialogVisible(false);
        setForceNotifyEpisodeId("");
        setForceNotifyConfirmData(null);
        // Ricarica i referti
        fetchReports();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: error.message || "Impossibile forzare la notifica",
        life: 5000,
      });
    } finally {
      setForceNotifyLoading(false);
    }
  };

  // Visualizza PDF in nuova scheda
  const handleViewPdf = async (episodeId: string, documentId?: string) => {
    if (!username) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Username non disponibile",
        life: 3000,
      });
      return;
    }

    const loadingKey = documentId ? `${episodeId}_${documentId}` : episodeId;
    setLoadingPdf(loadingKey);

    try {
      // Usa il metodo originale per ottenere il blob
      const blob = await downloadReport(episodeId, username, documentId);

      if (!blob) {
        throw new Error("Impossibile scaricare il referto");
      }

      // Crea URL direttamente dal blob
      const pdfUrl = URL.createObjectURL(blob);

      // Apri in nuova scheda
      window.open(pdfUrl, "_blank");

      // Salva anche in cache per download diretto
      setPdfCache((prev) => ({ ...prev, [episodeId]: pdfUrl }));
    } catch (error) {
      logger.error("Errore visualizzazione PDF:", error);
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Impossibile visualizzare il referto",
        life: 3000,
      });
    } finally {
      setLoadingPdf(null);
    }
  };

  // Download diretto PDF
  const handleDownloadPdf = async (episodeId: string, documentId?: string) => {
    if (!username) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Username non disponibile",
        life: 3000,
      });
      return;
    }

    const loadingKey = documentId ? `${episodeId}_${documentId}` : episodeId;
    setLoadingPdf(loadingKey);

    try {
      const blob = await downloadReport(episodeId, username, documentId);

      if (!blob) {
        throw new Error("Impossibile scaricare il referto");
      }

      // Genera nome file con numero accettazione e timestamp in formato ddMMyyyyHHmmss
      const now = new Date();
      const timestamp = `${String(now.getDate()).padStart(2, "0")}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${now.getFullYear()}${String(now.getHours()).padStart(
        2,
        "0"
      )}${String(now.getMinutes()).padStart(2, "0")}${String(
        now.getSeconds()
      ).padStart(2, "0")}`;
      const fileName = `${episodeId}-${timestamp}.pdf`;

      // Download diretto con nome personalizzato
      saveAs(blob, fileName);

      toast.current?.show({
        severity: "success",
        summary: "Download completato",
        detail: "Il referto è stato scaricato",
        life: 2000,
      });
    } catch (error) {
      logger.error("Errore download PDF:", error);
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Impossibile scaricare il referto",
        life: 3000,
      });
    } finally {
      setLoadingPdf(null);
    }
  };

  // SignalR refs
  const signalRConnections = useRef<Record<string, signalR.HubConnection>>({});

  // Download tutti i PDF di un gruppo
  const handleDownloadAllPdfs = async (group: Report[]) => {
    const zip = new JSZip();

    try {
      // Genera timestamp una volta sola per tutto il gruppo
      const now = new Date();
      const timestamp = `${String(now.getDate()).padStart(2, "0")}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${now.getFullYear()}${String(now.getHours()).padStart(
        2,
        "0"
      )}${String(now.getMinutes()).padStart(2, "0")}${String(
        now.getSeconds()
      ).padStart(2, "0")}`;

      for (let index = 0; index < group.length; index++) {
        const report = group[index];
        const blob = await downloadReport(
          report.episodeId,
          username!,
          report.id
        );

        if (blob) {
          // Usa lo stesso formato del download singolo
          const fileName = `${report.episodeId}-${timestamp}_${index + 1}.pdf`;
          zip.file(fileName, blob);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      // Nome dello zip con lo stesso formato
      saveAs(content, `Referti_${group[0].episodeId}-${timestamp}.zip`);

      toast.current?.show({
        severity: "success",
        summary: "Download completato",
        detail: `Scaricati ${group.length} referti`,
        life: 3000,
      });
    } catch (error) {
      logger.error("Errore download multiplo:", error);
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Impossibile scaricare i referti",
        life: 3000,
      });
    }
  };

  // Download DICOM con SignalR (manteniamo questa parte come era)
  const handleDownloadDicomZip = async (
    episodeId: string,
    studyUID: string,
    patientID: string
  ) => {
    try {
      if (!token) {
        throw new Error("Token non disponibile");
      }

      // Avvia stato download
      setDicomJobProgress((prev) => ({
        ...prev,
        [studyUID]: {
          status: "Avvio download...",
          progress: 5,
          downloading: true,
        },
      }));

      // Crea SignalR connection
      const conn = new signalR.HubConnectionBuilder()
        .withUrl(getSignalRExportJobHubUrl(), {
          accessTokenFactory: () => token,
          // Forza LongPolling (IIS/ARR può avere problemi con WebSocket)
          transport: signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
        })
        .configureLogging(signalR.LogLevel.Debug)
        .withAutomaticReconnect()
        .build();

      signalRConnections.current[studyUID] = conn;

      conn.on("ProgressChanged", (jobStatus: LocalDicomJobProgress) => {
        logger.log(`[SignalR][ProgressChanged][${studyUID}]`, jobStatus);

        if (jobStatus.status === "ready" && (jobStatus as any).zipFilePath) {
          // Download ZIP al termine
          fetch(
            `${getDownloadZipUrl()}?jobId=${encodeURIComponent(
              jobStatus.jobId || ""
            )}`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
            }
          )
            .then((res) => res.blob())
            .then((blob) => {
              saveAs(blob, `ImmaginiDICOM_${episodeId}.zip`);
              toast.current?.show({
                severity: "success",
                summary: "Download completato",
                detail: "Le immagini DICOM sono state scaricate",
                life: 3000,
              });
            })
            .catch((err) => {
              logger.error("Errore download ZIP:", err);
              toast.current?.show({
                severity: "error",
                summary: "Errore download",
                detail: "Impossibile scaricare il file ZIP",
                life: 4000,
              });
            })
            .finally(() => {
              setDicomJobProgress((prev) => ({
                ...prev,
                [studyUID]: {
                  ...prev[studyUID],
                  status: "Download completato",
                  progress: 100,
                  downloading: false,
                },
              }));
              // Chiudi SignalR
              if (signalRConnections.current[studyUID]) {
                signalRConnections.current[studyUID].stop();
                delete signalRConnections.current[studyUID];
              }
            });
        } else if (jobStatus.status === "failed") {
          setDicomJobProgress((prev) => ({
            ...prev,
            [studyUID]: {
              ...prev[studyUID],
              status: "Errore: " + ((jobStatus as any).errorMessage || ""),
              progress: 100,
              error: (jobStatus as any).errorMessage,
              downloading: false,
            },
          }));
          // Chiudi SignalR
          if (signalRConnections.current[studyUID]) {
            signalRConnections.current[studyUID].stop();
            delete signalRConnections.current[studyUID];
          }
        } else {
          // Aggiorna progresso
          const progress = jobStatus.progress || 10;
          setDicomJobProgress((prev) => ({
            ...prev,
            [studyUID]: {
              ...prev[studyUID],
              status: `Elaborazione in corso... ${progress}%`,
              progress: progress,
              downloading: true,
            },
          }));
        }
      });

      // Start SignalR
      logger.log("[SignalR] Tentativo di connessione...");
      try {
        await conn.start();
        logger.log("[SignalR] Connesso con successo!");
      } catch (err) {
        logger.error("[SignalR] Errore connessione:", err);
        throw new Error(`Impossibile connettersi a SignalR: ${err}`);
      }

      // Avvia job PACS - usa FormData invece di JSON
      const formData = new FormData();
      formData.append("studyUID", studyUID);
      formData.append("patientID", patientID);

      const publishResponse = await fetch(getPublishAndDownloadJobUrl(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // NON includere Content-Type per FormData - il browser lo imposta automaticamente
        },
        body: formData,
      });

      if (!publishResponse.ok) {
        throw new Error("Errore avvio job PACS");
      }

      const responseData = await publishResponse.json();
      const jobId =
        responseData.jobId || responseData.aJobs?.[0]?.sID || responseData.sID;

      if (!jobId) {
        throw new Error("JobId non restituito dal backend");
      }

      logger.log(`[PACS] Job avviato: ${jobId}`);

      // Sottoscrivi al job per ricevere aggiornamenti
      await conn.invoke("SubscribeToJob", jobId);
      logger.log(`[SignalR] Sottoscritto al job: ${jobId}`);

      setDicomJobProgress((prev) => ({
        ...prev,
        [studyUID]: {
          ...prev[studyUID],
          status: "Attesa esportazione PACS...",
          progress: 10,
          jobId,
          downloading: true,
        },
      }));

      // Fallback: dopo 15 secondi, prova comunque a scaricare il file
      // (il PACS potrebbe aver completato ma SignalR/polling non funziona)
      setTimeout(() => {
        const currentProgress = dicomJobProgress[studyUID];
        if (
          currentProgress &&
          currentProgress.downloading &&
          currentProgress.status.includes("Attesa")
        ) {
          logger.log("[FALLBACK] Tentativo download dopo timeout");

          // Prova a scaricare il file
          fetch(
            `${getDownloadZipUrl()}?jobId=${encodeURIComponent(jobId)}`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
            }
          )
            .then((res) => {
              if (res.ok) {
                return res.blob();
              }
              throw new Error("File non ancora pronto");
            })
            .then((blob) => {
              saveAs(blob, `ImmaginiDICOM_${episodeId}.zip`);
              toast.current?.show({
                severity: "success",
                summary: "Download completato",
                detail: "Le immagini DICOM sono state scaricate",
                life: 3000,
              });
              setDicomJobProgress((prev) => ({
                ...prev,
                [studyUID]: {
                  status: "Download completato",
                  progress: 100,
                  downloading: false,
                },
              }));
              // Chiudi SignalR
              if (signalRConnections.current[studyUID]) {
                signalRConnections.current[studyUID].stop();
                delete signalRConnections.current[studyUID];
              }
            })
            .catch((err) => {
              logger.error("[FALLBACK] Errore download:", err);
              // Continua ad aspettare SignalR
              setDicomJobProgress((prev) => ({
                ...prev,
                [studyUID]: {
                  ...prev[studyUID],
                  status: "Elaborazione in corso... (attesa prolungata)",
                  progress: 50,
                },
              }));
            });
        }
      }, 600000); // 10 minuti di timeout per supportare download DICOM di grandi dimensioni
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Errore sconosciuto";
      setDicomJobProgress((prev) => ({
        ...prev,
        [studyUID]: {
          status: errorMessage,
          progress: 100,
          error: errorMessage,
          downloading: false,
        },
      }));
      // Logging dettagliato su errori
      logger.error("[handleDownloadDicomZip] CATCH Error", error, {
        studyUID,
        patientID,
        episodeId,
      });
      toast.current?.show({
        severity: "error",
        summary: "Errore Download DICOM",
        detail: `Errore download immagini DICOM: ${errorMessage}`,
        life: 5000,
      });
    }
  };

  // Scarica tutte le immagini di un gruppo (aggregata!)
  const handleDownloadAllDicom = async (episodeId: string) => {
    const studyUIDs = studyUIDsMap[episodeId] || [];
    const patientId = patientIdMap[episodeId];

    if (!patientId || studyUIDs.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Nessuna immagine",
        detail: "Non ci sono immagini DICOM da scaricare",
        life: 3000,
      });
      return;
    }

    try {
      // Crea un'istanza JSZip
      const zip = new JSZip();

      for (const studyUID of studyUIDs) {
        await handleDownloadDicomZip(episodeId, studyUID, patientId);
      }

      toast.current?.show({
        severity: "info",
        summary: "Download avviato",
        detail: `Download di ${studyUIDs.length} serie DICOM avviato`,
        life: 3000,
      });
    } catch (error) {
      logger.error("Errore download multiplo DICOM:", error);
    }
  };

  // Ripubblica referto (con conferma via email)
  const handleRepublishReport = async (episodeId: string, report?: any) => {
    console.log("🔄 Requesting republish with email confirmation for episodeId:", episodeId);

    // Imposta lo stato di ripubblicazione in corso
    setRepublishInProgress(episodeId);
    setLastRepublishedEpisodeId(null);

    try {
      const url = getRepublishRequestUrl();
      console.log("🔄 Calling URL:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          examinationMnemonicCodeFull: episodeId,
          username: username,
        }),
      });

      let data;
      try {
        const responseText = await response.text();
        try {
          data = JSON.parse(responseText);
        } catch {
          data = { message: responseText };
        }
      } catch {
        data = {};
      }

      if (response.ok) {
        setRepublishInProgress(null);
        toast.current?.show({
          severity: "info",
          summary: "Email inviata",
          detail:
            data.message ||
            "Ti abbiamo inviato un'email di conferma. Controlla la tua casella di posta e clicca sul link per completare la ripubblicazione.",
          life: 6000,
        });
      } else {
        setRepublishInProgress(null);
        toast.current?.show({
          severity: "error",
          summary: "Errore",
          detail:
            data.message ||
            data.error ||
            "Impossibile richiedere la ripubblicazione del referto.",
          life: 4000,
        });
      }
    } catch (err: unknown) {
      setRepublishInProgress(null);
      const errorMessage =
        err instanceof Error ? err.message : "Errore sconosciuto";
      toast.current?.show({
        severity: "error",
        summary: "Errore di rete",
        detail: errorMessage,
        life: 4000,
      });
    }
  };

  const formatDate = (isoString: string | Date | undefined) => {
    if (!isoString) return "N/A";

    let date: Date;
    if (isoString instanceof Date) {
      date = isoString;
    } else if (isoString.includes("/")) {
      // Formato dd/MM/yyyy
      const parts = isoString.split(/[\/ :]/);
      if (parts.length >= 3) {
        date = new Date(
          +parts[2],
          +parts[1] - 1,
          +parts[0],
          parts[3] ? +parts[3] : 0,
          parts[4] ? +parts[4] : 0
        );
      } else {
        date = new Date(isoString);
      }
    } else {
      date = new Date(isoString);
    }

    if (isNaN(date.getTime())) return isoString.toString();

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatDateOnly = (isoString: string | Date | undefined) => {
    if (!isoString) return "N/A";

    let date: Date;
    if (isoString instanceof Date) {
      date = isoString;
    } else if (isoString.includes("/")) {
      // Formato dd/MM/yyyy
      const parts = isoString.split(/[\/ :]/);
      if (parts.length >= 3) {
        date = new Date(
          +parts[2],
          +parts[1] - 1,
          +parts[0],
          parts[3] ? +parts[3] : 0,
          parts[4] ? +parts[4] : 0
        );
      } else {
        date = new Date(isoString);
      }
    } else {
      date = new Date(isoString);
    }

    if (isNaN(date.getTime())) return isoString.toString();

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Cleanup
  useEffect(() => {
    return () => {
      // Cleanup SignalR connections
      Object.values(signalRConnections.current).forEach((conn) => {
        conn.stop();
      });
      // Cleanup PDF URLs
      Object.values(pdfCache).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      // Cleanup polling intervals
      Object.values(jobPollingIntervals).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, [pdfCache, jobPollingIntervals]);

  // Render
  return (
    <div className="page-container">
      <Toast ref={toast} />

      <div className="page-content">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
          }}
        >
          <div>
            {!loading && (
              <Button
                icon="pi pi-refresh"
                className="p-button-rounded p-button-text"
                label="Aggiorna lista referti"
                style={{ color: "var(--primary-color, #229a61)" }}
                onClick={() => {
                  fetchReports();
                }}
              />
            )}
          </div>
          <div>
            {isAdmin() && (
              <Button
                icon="pi pi-send"
                className="p-button-rounded p-button-text"
                label="Forza Notifica (Admin)"
                style={{ color: "#f59e0b" }}
                onClick={() => setForceNotifyDialogVisible(true)}
              />
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "2rem" }}
            ></i>
            <p>Caricamento referti in corso...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <i className="pi pi-exclamation-triangle"></i>
            <p>{error}</p>
            <Button
              label="Riprova"
              icon="pi pi-refresh"
              onClick={fetchReports}
              className="p-button-sm"
            />
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className="no-reports">
            <i
              className="pi pi-inbox"
              style={{ fontSize: "3rem", color: "#999" }}
            ></i>
            <p>Nessun referto disponibile</p>
          </div>
        )}

        {!loading && reports.length > 0 && (
          <div className="reports-grid">
            {(() => {
              // Raggruppa i referti per episodeId
              const groupedByEpisode = reports.reduce((acc, report) => {
                if (!acc[report.episodeId]) acc[report.episodeId] = [];
                acc[report.episodeId].push(report);
                return acc;
              }, {} as Record<string, Report[]>);

              return Object.entries(groupedByEpisode).map(
                ([episodeId, group]) => {
                  const firstReport = group[0];
                  const hasMultiple = group.length > 1;
                  const someExpired = group.some((report) =>
                    isReportExpired(report.publishDate)
                  );
                  const allExpired = group.every((report) =>
                    isReportExpired(report.publishDate)
                  );
                  const someRepublished = group.some((report) =>
                    isRepublished(report.publishDate, report.registrationDate)
                  );
                  // Usa il nuovo stato dicomAvailability invece di controllare studyUIDsMap
                  const dicomAvailable =
                    dicomAvailability[firstReport.episodeId] === true;
                  const dicomProgress =
                    dicomJobProgress[
                      studyUIDsMap[firstReport.episodeId]?.[0] || ""
                    ];

                  return (
                    <Card
                      key={episodeId}
                      className={`page-card ${
                        hasMultiple ? "grouped-card" : ""
                      }`}
                    >
                      {Object.keys(groupedByEpisode).indexOf(episodeId) ===
                        0 && (
                        <>
                          <h2
                            style={{
                              color: "var(--primary-color, #229a61)",
                              marginBottom: "1.5rem",
                              paddingBottom: "1rem",
                              borderBottom: "2px solid var(--primary-accent, #4caf50)",
                            }}
                          >
                            Referti Online
                          </h2>
                        </>
                      )}
                      <div className="report-header">
                        <h3>
                          Accettazione del{" "}
                          {formatDateOnly(firstReport.registrationDate)}
                          <br />
                          N. {episodeId}
                        </h3>
                        {hasMultiple && (
                          <span className="badge badge-grouped">
                            Referti multipli ({group.length})
                          </span>
                        )}
                        {allExpired && !someRepublished && (
                          <span className="badge badge-expired">Scaduto</span>
                        )}
                        {someRepublished && (
                          <span className="badge badge-republished">
                            Ripubblicato
                          </span>
                        )}
                      </div>

                      <div className="report-info">
                        {hasMultiple ? (
                          <>
                            <details
                              className="report-details"
                              style={{ marginTop: "1rem" }}
                            >
                              <summary
                                style={{
                                  cursor: "pointer",
                                  color: "#007ad9",
                                  fontWeight: "600",
                                }}
                              >
                                <i
                                  className="pi pi-list"
                                  style={{ marginRight: "0.5rem" }}
                                ></i>
                                Mostra dettagli referti ({group.length})
                              </summary>
                              <div
                                className="report-info-grid"
                                style={{ marginTop: "1rem" }}
                              >
                                {group.map((report, index) => (
                                  <div
                                    key={`${report.episodeId}-${index}`}
                                    className="singolo-referto-div"
                                  >
                                    <p
                                      style={{
                                        fontWeight: "600",
                                        marginBottom: "0.5rem",
                                      }}
                                    >
                                      <i className="pi pi-file"></i>
                                      {report.examName ||
                                        `Referto ${index + 1}`}
                                    </p>
                                    <p
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <i className="pi pi-calendar"></i>
                                      <span style={{ marginLeft: "0.5rem" }}>
                                        Data registrazione:
                                      </span>
                                      <span style={{ marginLeft: "0.5rem" }}>
                                        {formatDateOnly(
                                          report.registrationDate
                                        )}
                                      </span>
                                    </p>
                                    <p
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <i className="pi pi-calendar-plus"></i>
                                      <span style={{ marginLeft: "0.5rem" }}>
                                        Pubblicato:
                                      </span>
                                      <span style={{ marginLeft: "0.5rem" }}>
                                        {formatDateOnly(report.publishDate)}
                                      </span>
                                    </p>
                                    {report.workareasString && (
                                      <p>
                                        <i className="pi pi-tag"></i> Area:{" "}
                                        {report.workareasString}
                                      </p>
                                    )}
                                    {isReportExpired(report.publishDate) &&
                                      !isRepublished(
                                        report.publishDate,
                                        report.registrationDate
                                      ) && (
                                        <span
                                          className="badge badge-expired"
                                          style={{ marginTop: "0.5rem" }}
                                        >
                                          Scaduto
                                        </span>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </details>
                          </>
                        ) : (
                          <div className="report-info-grid">
                            <div className="singolo-referto-div">
                              <p
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <i className="pi pi-calendar"></i>
                                <span
                                  style={{
                                    marginLeft: "0.5rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Pubblicato il:
                                </span>
                                <span style={{ marginLeft: "0.5rem" }}>
                                  {formatDateOnly(firstReport.publishDate)}
                                </span>
                              </p>
                            </div>
                            {firstReport.workareasString && (
                              <div className="singolo-referto-div">
                                <p
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <i className="pi pi-tag"></i>
                                  <span
                                    style={{
                                      marginLeft: "0.5rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Area:
                                  </span>
                                  <span style={{ marginLeft: "0.5rem" }}>
                                    {firstReport.workareasString}
                                  </span>
                                </p>
                              </div>
                            )}
                            <div className="singolo-referto-div">
                              <p
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <i className="pi pi-id-card"></i>
                                <span
                                  style={{
                                    marginLeft: "0.5rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Codice:
                                </span>
                                <span style={{ marginLeft: "0.5rem" }}>
                                  {firstReport.episodeId}
                                </span>
                              </p>
                            </div>
                            {firstReport.examinationState !== undefined &&
                              (firstReport.examinationState === 5 ||
                                firstReport.examinationState === 2) && (
                                <div className="singolo-referto-div">
                                  <p
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <i className="pi pi-info-circle"></i>
                                    <span
                                      style={{
                                        marginLeft: "0.5rem",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Stato:
                                    </span>
                                    <span style={{ marginLeft: "0.5rem" }}>
                                      {firstReport.examinationState === 5
                                        ? "Completo"
                                        : firstReport.examinationState === 2
                                        ? "Parziale"
                                        : "N/D"}
                                    </span>
                                  </p>
                                </div>
                              )}
                          </div>
                        )}
                      </div>

                      <div className="report-actions">
                        {/* Azioni per singolo referto o gruppo */}
                        {hasMultiple ? (
                          <>
                            {/* Gruppo multiplo - mostra azioni per ogni referto */}
                            {group.map((report, index) => {
                              const expired = isReportExpired(
                                report.publishDate
                              );
                              const republished = isRepublished(
                                report.publishDate,
                                report.registrationDate
                              );

                              return (
                                <div
                                  key={`${report.episodeId}-${index}`}
                                  className="report-action-group"
                                >
                                  <h4>
                                    {report.examName || `Referto ${index + 1}`}
                                  </h4>
                                  {report.workareasString && (
                                    <p
                                      style={{
                                        fontSize: "0.9rem",
                                        color: "#666",
                                        marginBottom: "0.5rem",
                                      }}
                                    >
                                      <i
                                        className="pi pi-tag"
                                        style={{ marginRight: "0.5rem" }}
                                      ></i>
                                      Area: {report.workareasString}
                                    </p>
                                  )}
                                  {!expired ? (
                                    <div className="report-action-group">
                                      <Button
                                        label="Visualizza"
                                        icon="pi pi-eye"
                                        onClick={() =>
                                          handleViewPdf(
                                            report.episodeId,
                                            report.id
                                          )
                                        }
                                        loading={
                                          loadingPdf ===
                                          `${report.episodeId}_${report.id}`
                                        }
                                        className="p-button-primary p-button-sm appuntamenti-btn view-btn"
                                        style={{ marginBottom: "1rem" }}
                                        disabled={
                                          !!loadingPdf &&
                                          loadingPdf !==
                                            `${report.episodeId}_${report.id}`
                                        }
                                      />
                                      <Button
                                        label="Scarica"
                                        icon="pi pi-download"
                                        onClick={() =>
                                          handleDownloadPdf(
                                            report.episodeId,
                                            report.id
                                          )
                                        }
                                        loading={
                                          loadingPdf ===
                                          `${report.episodeId}_${report.id}`
                                        }
                                        className="p-button-success p-button-sm appuntamenti-btn download-btn"
                                        style={{ marginBottom: "1rem" }}
                                        disabled={
                                          !!loadingPdf &&
                                          loadingPdf !==
                                            `${report.episodeId}_${report.id}`
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <div className="report-action-group">
                                      <Button
                                        label="Ripubblica"
                                        icon="pi pi-refresh"
                                        onClick={() =>
                                          handleRepublishReport(
                                            report.episodeId,
                                            report
                                          )
                                        }
                                        className="p-button-warning p-button-sm appuntamenti-btn"
                                        style={{ marginBottom: "1rem" }}
                                        disabled={
                                          republishInProgress ===
                                          report.episodeId
                                        }
                                        loading={
                                          republishInProgress ===
                                          report.episodeId
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Pulsante download tutti i PDF del gruppo */}
                            {!allExpired && (
                              <div className="report-action-group">
                                <Button
                                  label="Scarica tutti i PDF"
                                  icon="pi pi-download"
                                  onClick={() => handleDownloadAllPdfs(group)}
                                  className="p-button-success p-button-sm appuntamenti-btn download-all-btn full-width"
                                  style={{
                                    marginTop: "1rem",
                                    marginBottom: "1rem",
                                  }}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Singolo referto */}
                            {!isReportExpired(firstReport.publishDate) ? (
                              <div className="report-action-group">
                                <Button
                                  label="Visualizza PDF"
                                  icon="pi pi-eye"
                                  onClick={() =>
                                    handleViewPdf(
                                      firstReport.episodeId,
                                      firstReport.id
                                    )
                                  }
                                  loading={
                                    loadingPdf ===
                                    `${firstReport.episodeId}_${firstReport.id}`
                                  }
                                  className="p-button-primary p-button-sm appuntamenti-btn view-btn"
                                  disabled={
                                    !!loadingPdf &&
                                    loadingPdf !==
                                      `${firstReport.episodeId}_${firstReport.id}`
                                  }
                                />
                                <Button
                                  label="Scarica PDF"
                                  icon="pi pi-download"
                                  onClick={() =>
                                    handleDownloadPdf(
                                      firstReport.episodeId,
                                      firstReport.id
                                    )
                                  }
                                  loading={
                                    loadingPdf ===
                                    `${firstReport.episodeId}_${firstReport.id}`
                                  }
                                  className="p-button-success p-button-sm appuntamenti-btn download-btn"
                                  disabled={
                                    !!loadingPdf &&
                                    loadingPdf !==
                                      `${firstReport.episodeId}_${firstReport.id}`
                                  }
                                />
                              </div>
                            ) : (
                              <div className="report-action-group">
                                <Button
                                  label="Ripubblica"
                                  icon="pi pi-refresh"
                                  onClick={() =>
                                    handleRepublishReport(
                                      firstReport.episodeId,
                                      firstReport
                                    )
                                  }
                                  className="p-button-warning p-button-sm appuntamenti-btn"
                                  style={{ marginBottom: "1rem" }}
                                  disabled={
                                    republishInProgress ===
                                    firstReport.episodeId
                                  }
                                  loading={
                                    republishInProgress ===
                                    firstReport.episodeId
                                  }
                                />
                              </div>
                            )}
                          </>
                        )}

                        {/* DICOM Actions - mostra se ci sono immagini DICOM disponibili */}
                        {dicomAvailable && (
                          <div className="dicom-section">
                            <p className="dicom-info">
                              <i className="pi pi-images"></i>
                              <span style={{ marginLeft: "8px" }}>
                                Immagini DICOM disponibili per il download
                              </span>
                            </p>

                            {!dicomProgress?.downloading && (
                              <div className="report-action-group">
                                <Button
                                  label="Download Immagini"
                                  icon="pi pi-images"
                                  onClick={() =>
                                    handleDownloadAllDicom(
                                      firstReport.episodeId
                                    )
                                  }
                                  className="p-button-warning p-button-sm appuntamenti-btn dicom-btn"
                                  style={{
                                    marginTop: "1rem",
                                    marginBottom: "1rem",
                                  }}
                                />
                                <div
                                  style={{
                                    marginTop: "10px",
                                    padding: "10px",
                                    backgroundColor: "#fff3cd",
                                    borderRadius: "4px",
                                    fontSize: "0.85rem",
                                    color: "#856404",
                                  }}
                                >
                                  <i
                                    className="pi pi-exclamation-triangle"
                                    style={{
                                      marginRight: "8px",
                                      color: "#f0ad4e",
                                    }}
                                  ></i>
                                  Le immagini sono presenti anche nel CD
                                  consegnato insieme alla cartellina
                                  ritirata in struttura
                                  <br />
                                  Il download potrebbe richiedere alcuni minuti
                                  a seconda della tipologia di esame e della
                                  grandezza delle immagini. Una volta effettuato
                                  il download è necessario decomprimere il file
                                  zip in una cartella e successivamente avviare
                                  ApriMediaWindows oppure ApriMediaMac, a
                                  seconda del sistema operativo.
                                </div>
                              </div>
                            )}

                            {dicomProgress?.downloading && (
                              <div className="dicom-progress">
                                <p>{dicomProgress.status}</p>
                                <div className="progress-bar">
                                  <div
                                    className="progress-fill"
                                    style={{
                                      width: `${dicomProgress.progress}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {dicomProgress?.error && (
                              <p className="error-text">
                                <i className="pi pi-exclamation-circle"></i>
                                {dicomProgress.error}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                }
              );
            })()}
          </div>
        )}

        {/* Dialog Forza Notifica (Admin) */}
        <Dialog
          header="Forza Notifica Referto (Solo Amministratori)"
          visible={forceNotifyDialogVisible}
          style={{ width: "550px" }}
          onHide={() => {
            if (!forceNotifyLoading) {
              setForceNotifyDialogVisible(false);
              setForceNotifyEpisodeId("");
              setForceNotifyConfirmData(null);
            }
          }}
          footer={
            <div>
              <Button
                label="Annulla"
                icon="pi pi-times"
                onClick={() => {
                  setForceNotifyDialogVisible(false);
                  setForceNotifyEpisodeId("");
                  setForceNotifyConfirmData(null);
                }}
                className="p-button-text"
                disabled={forceNotifyLoading}
              />
              {!forceNotifyConfirmData ? (
                <Button
                  label="Verifica"
                  icon="pi pi-search"
                  onClick={handleForceNotify}
                  loading={forceNotifyLoading}
                  className="p-button-info"
                />
              ) : (
                <Button
                  label="Conferma Notifica"
                  icon="pi pi-check"
                  onClick={confirmForceNotify}
                  loading={forceNotifyLoading}
                  className="p-button-success"
                />
              )}
            </div>
          }
        >
          <div className="force-notify-form">
            <p>
              Inserisci il codice accettazione (ExaminationMnemonicCodeFull) del
              referto da notificare immediatamente.
            </p>

            <div className="input-group" style={{ marginTop: "20px" }}>
              <FloatLabel>
                <InputText
                  id="forceNotifyEpisodeId"
                  value={forceNotifyEpisodeId}
                  onChange={(e) => setForceNotifyEpisodeId(e.target.value)}
                  disabled={forceNotifyLoading || !!forceNotifyConfirmData}
                  onKeyPress={(e) => e.key === "Enter" && handleForceNotify()}
                  style={{ width: "100%" }}
                />
                <label htmlFor="forceNotifyEpisodeId">
                  Codice Accettazione
                </label>
              </FloatLabel>
            </div>

            {forceNotifyConfirmData && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: "#fff3cd",
                  borderRadius: "4px",
                  border: "1px solid #ffc107",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    marginBottom: "10px",
                    fontSize: "0.95em",
                    color: "#856404",
                    fontWeight: "bold",
                  }}
                >
                  <i
                    className="pi pi-exclamation-triangle"
                    style={{ marginRight: "8px" }}
                  ></i>
                  {forceNotifyConfirmData.message}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.9em",
                    color: "#856404",
                  }}
                >
                  Trovati <strong>{forceNotifyConfirmData.count}</strong>{" "}
                  referto/i:
                </p>
                <ul
                  style={{
                    marginTop: "10px",
                    fontSize: "0.85em",
                    color: "#666",
                  }}
                >
                  {forceNotifyConfirmData.reports?.map(
                    (report: any, index: number) => (
                      <li key={index}>
                        ID: {report.id} - Episodio: {report.episodeId}
                        {report.scheduledDate &&
                          ` - Programmato: ${new Date(
                            report.scheduledDate
                          ).toLocaleString("it-IT")}`}
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

            <div
              style={{
                marginTop: "20px",
                padding: "10px",
                backgroundColor: "#e3f2fd",
                borderRadius: "4px",
                border: "1px solid #2196f3",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "0.9em",
                  color: "#1976d2",
                }}
              >
                <i
                  className="pi pi-info-circle"
                  style={{ marginRight: "5px" }}
                ></i>
                <strong>Nota:</strong> Questa operazione imposterà il referto
                come notificato (NotificationSent=1, NotificationSentDate=Now)
                e lo renderà immediatamente visibile nel portale paziente.
              </p>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default RefertiOnline;
